/**
 * Streaming chat client for the Lambda Function URL.
 *
 * Reads SSE events from the response body and invokes onToken / onDone /
 * onError callbacks as data arrives. The Function URL is separate from
 * the main API Gateway URL and uses the same Cognito JWT for auth.
 *
 * v1 shape: { personaId, conversationId, message }
 * 7g adds optional projectId in the body for project-scoped chats.
 * The chat Lambda treats a missing projectId as byte-identical to v1.
 *
 * The onError callback receives an Error. For budget-exhausted errors
 * the Error has a tagged `kind: "budget"` property and extra fields
 * (`tokensUsedToday`, `dailyTokenBudget`) so the UI can render a
 * distinct error bubble variant without parsing the message string.
 */

import { streamSSE, taggedBudgetError } from "./sse.js";

const CHAT_ENDPOINT = import.meta.env.VITE_CHAT_ENDPOINT.replace(/\/$/, "");

export async function streamChat({
  personaId,
  conversationId,
  projectId,
  message,
  documentFilename,
  documentContent,
  signal,
  onToken,
  onDone,
  onError,
  onToolCall,
  onToolResult,
  onWarning,
}) {
  const body = { personaId, conversationId, message };
  if (projectId) body.projectId = projectId;
  if (documentFilename) body.documentFilename = documentFilename;
  if (typeof documentContent === "string") body.documentContent = documentContent;

  await streamSSE({
    url: CHAT_ENDPOINT,
    body,
    signal,
    onError,
    onEvent(data) {
      if (data.error) {
        const err = taggedBudgetError(data) || new Error(data.error);
        return { bail: true, error: err };
      } else if (data.type === "tool_call") {
        onToolCall?.(data);
      } else if (data.type === "tool_result") {
        onToolResult?.(data);
      } else if (data.type === "warning") {
        onWarning?.(data);
      } else if (data.done) {
        onDone?.(data);
      } else if (data.token) {
        onToken?.(data.token);
      }
    },
  });
}
